import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip
} from "chart.js";
import { Bar } from "react-chartjs-2";
import axios from "axios";
import {
  FormControl,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  CircularProgress,
  Typography
} from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip
);

export default function FullHeightBarChart() {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState("day");
  const [selectedTab, setSelectedTab] = useState("sales");
  const [loading, setLoading] = useState(false);

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const endpoint = selectedTab === "sales"
        ? "soldAndbrought/total-sales"
        : "soldAndbrought/total-buy";
      const response = await axios.post(endpoint, { timeline });

      setData({
        labels: Object.keys(response.data).map(label => {
          // Format labels based on timeline
          if (timeline === "day") return new Date(label).toLocaleDateString();
          if (timeline === "week") return `Week ${label}`;
          if (timeline === "month") return new Date(label).toLocaleString('default', { month: 'short' });
          if (timeline === "year") return label;
          return label;
        }),
        datasets: [{
          label: selectedTab === "sales" ? "Sales" : "Purchases", // Add dataset label
          data: Object.values(response.data),
          backgroundColor: selectedTab === "sales"
            ? "rgba(198, 64, 145, 0.8)"
            : "rgba(34, 97, 162, 0.8)",
          borderRadius: 6,
          borderSkipped: false,
        }],
      });
    } catch (error) {
      console.error("Error fetching chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [timeline, selectedTab]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        bodyFont: { size: 12, family: "Poppins" },
        titleFont: { size: 12, family: "Poppins", weight: "bold" },
        displayColors: false,
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.raw.toLocaleString()}`;
          },
          title: (context) => {
            return context[0].label || ''; // Safely access label
          }
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: "#666",
          font: {
            family: "Poppins",
            size: 12
          }
        },
        border: { display: false }
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
        ticks: {
          callback: (value) => value.toLocaleString(),
          color: "#666",
          font: {
            family: "Poppins",
            size: 12
          }
        },
        border: { display: false }
      },
    },
    animation: {
      duration: 800,
    },
  };

  return (
    <Box sx={{
      p: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: 2,
        gap: 1,
        flexWrap: 'wrap'
      }}>
        <ToggleButtonGroup
          size="small"
          value={selectedTab}
          exclusive
          onChange={(e, newVal) => newVal && setSelectedTab(newVal)}
          sx={{
            width: "200px",
            gap:"15px",
            '& .MuiToggleButtonGroup-grouped': {
              margin: '0 2px',
              border: 0,
              borderRadius: '8px !important',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '6px 12px',
              color: '#666',
              '&.Mui-selected': {
                backgroundColor: selectedTab === 'sales' ? '#c64091' : '#2261a2',
                color: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: selectedTab === 'sales' ? '#b53681' : '#1d528b',
                }
              },
              '&:not(.Mui-selected)': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                }
              },
              transition: 'all 0.2s ease-in-out',
            }
          }}
        >
          <ToggleButton value="sales" sx={{ minWidth: 80 }}>
            <Typography sx={{
              fontFamily: "Poppins",
              fontStyle: "normal",
              fontSize: "14px"
            }}>
              Sales
            </Typography>
          </ToggleButton>
          <ToggleButton value="purchases" sx={{ minWidth: 80 }}>
            <Typography sx={{
              fontFamily: "Poppins",
              fontStyle: "normal",
              fontSize: "14px"
            }}>
              Purchases
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={timeline}
            onChange={(e) => setTimeline(e.target.value)}
            sx={{ fontFamily: "Poppins" }}
          >
            <MenuItem value="day" sx={{ fontFamily: "Poppins" }}><Typography
              sx={{
                fontFamily: "Poppins",
                fontStyle: "normal",
                fontSize: "14px"
              }}
            >Daily</Typography></MenuItem>
            <MenuItem value="week" sx={{ fontFamily: "Poppins" }}><Typography sx={{
              fontFamily: "Poppins",
              fontStyle: "normal",
              fontSize: "14px"
            }}>Weekly </Typography> </MenuItem>
            <MenuItem value="month" sx={{ fontFamily: "Poppins" }}><Typography sx={{
              fontFamily: "Poppins",
              fontStyle: "normal",
              fontSize: "14px"
            }}>Monthly</Typography></MenuItem>
            <MenuItem value="year" sx={{ fontFamily: "Poppins" }}><Typography sx={{
              fontFamily: "Poppins",
              fontStyle: "normal",
              fontSize: "14px"
            }}>Yearly</Typography></MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{
        flex: 1,
        minHeight: 0,
        position: 'relative'
      }}>
        {loading && (
          <Box sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.7)'
          }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {data ? (
          <Bar
            data={data}
            options={options}
          />
        ) : (
          !loading && (
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              fontFamily: "Poppins"
            }}>
              No data available
            </Box>
          )
        )}
      </Box>
    </Box>
  );
}